import { useEffect, useMemo, useRef, useState } from "react";
import type { BatteryPoint, BatterySession } from "@fts/core";
import { niceTicks } from "../../lib/chart";

/**
 * Battery voltage over time (ADR 0034): the 15-minute average as a line, the min–max range as a band,
 * charge sessions shaded and the low-battery threshold as a dashed line. Measured values only; a gap in the
 * data (no bucket) breaks the line. The text alternative is `label` (the chart's name) plus the report table.
 */
export function VoltageChart({
  series,
  sessions,
  lowV,
  from,
  to,
  label,
  formatTime,
  bucketS,
}: {
  series: BatteryPoint[];
  sessions: BatterySession[];
  lowV: number | null;
  from: number;
  to: number;
  label: string;
  formatTime: (ms: number) => string;
  bucketS: number;
}) {
  // Drawn at the container's width (no stretched text on phones).
  const box = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(720);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const read = () => el.clientWidth > 0 && setW(Math.max(280, el.clientWidth));
    read();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const H = 220;
  const pad = { l: 44, r: 12, t: 10, b: 26 };
  const lay = useMemo(() => {
    const vals = series.flatMap((p) => [p.minV, p.maxV]).concat(lowV !== null ? [lowV] : []);
    const lo0 = vals.length ? Math.min(...vals) : 0;
    const hi0 = vals.length ? Math.max(...vals) : 1;
    const span = Math.max(1, hi0 - lo0);
    const lo = Math.floor(lo0 - span * 0.1);
    const ticks = niceTicks(hi0 + span * 0.1 - lo, 4).map((x) => x + lo);
    const hi = Math.max(ticks[ticks.length - 1] ?? hi0, hi0);
    const x = (ms: number) => pad.l + ((ms - from) / Math.max(1, to - from)) * (W - pad.l - pad.r);
    const y = (v: number) => pad.t + (1 - (v - lo) / Math.max(1e-9, hi - lo)) * (H - pad.t - pad.b);
    const pts = series.map((p) => ({ ms: Date.parse(p.at) + (bucketS * 1000) / 2, p }));
    const lines: string[] = [];
    let cur = "";
    let prev: number | null = null;
    for (const { ms, p } of pts) {
      const gap = prev !== null && ms - prev > bucketS * 1000 * 1.5;
      if (gap && cur) {
        lines.push(cur);
        cur = "";
      }
      cur += `${cur ? "L" : "M"}${x(ms).toFixed(1)},${y(p.avgV).toFixed(1)}`;
      prev = ms;
    }
    if (cur) lines.push(cur);
    const band = pts.map(({ ms, p }) => ({ x: x(ms), y1: y(p.maxV), y2: y(p.minV) }));
    const days: number[] = [];
    for (let d = from; d <= to; d += 86_400_000) days.push(d);
    return { x, y, lines, band, ticks: ticks.filter((t) => t >= lo && t <= hi), days };
  }, [series, lowV, from, to, bucketS, W]);
  const bw = Math.max(1.5, ((bucketS * 1000) / Math.max(1, to - from)) * (W - pad.l - pad.r));
  return (
    <div ref={box} className="voltage-chart-box">
    <svg className="voltage-chart" width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label}>
      {lay.ticks.map((v) => (
        <g key={v}>
          <line x1={pad.l} x2={W - pad.r} y1={lay.y(v)} y2={lay.y(v)} className="vc-grid" />
          <text x={pad.l - 6} y={lay.y(v) + 4} textAnchor="end" className="vc-axis">
            {v}
          </text>
        </g>
      ))}
      {sessions.map((s) => (
        <rect key={s.start} x={lay.x(Date.parse(s.start))} y={pad.t} width={Math.max(1, lay.x(Date.parse(s.end)) - lay.x(Date.parse(s.start)))} height={H - pad.t - pad.b} className={s.opportunity ? "vc-charge vc-opp" : "vc-charge"} />
      ))}
      {lay.band.map((b, i) => (
        <rect key={i} x={b.x - bw / 2} y={b.y1} width={bw} height={Math.max(0.5, b.y2 - b.y1)} className="vc-band" />
      ))}
      {lay.lines.map((d, i) => (
        <path key={i} d={d} className="vc-line" />
      ))}
      {lowV !== null && <line x1={pad.l} x2={W - pad.r} y1={lay.y(lowV)} y2={lay.y(lowV)} className="vc-low" />}
      {lay.days.filter((d) => lay.x(d) < W - 44).filter((_, i, all) => W >= 560 || all.length <= 4 || i % Math.ceil(all.length / 4) === 0).slice(0, 12).map((d) => (
        <text key={d} x={lay.x(d) + 2} y={H - 8} className="vc-axis">
          {formatTime(d)}
        </text>
      ))}
    </svg>
    </div>
  );
}
